import IconOverflow from '@apollo/icons/default/IconOverflow.svg';
import {
  Avatar,
  Group,
  IconButton,
  Menu,
  MenuButton,
  MenuContent,
  MenuItem,
  MenuList,
  Pagination,
  Table,
  TableContainer,
  TableSortButton,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@apollo/orbit';
import React from 'react';

import {
  LocalTimestamp,
  RelativeTimestamp,
} from 'src/components/timestamp/Timestamp';

import { BadgeCell } from './BadgeCell';

export interface Row {
  name: string;
  type: string;
  role: string;
  entity: string;
  createdBy: string;
  createdAt: string;
  lastUsed: string;
  status: string;
}

interface ApiKeyTableProps {
  rows: Row[];
  currentPage: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onSort: (sortBy: keyof Row, sortOrder: 'asc' | 'desc') => void;
  onRename?: (row: Row) => void;
  onRevoke?: (row: Row) => void;
}

const getStatusVariant = (status: string) => {
  let variant = 'neutralOutlined';
  if (status.toLowerCase() === 'active') {
    variant = 'success';
  } else if (status.toLowerCase() === 'expired') {
    variant = 'warn';
  } else if (status.toLowerCase() === 'revoked') {
    variant = 'error';
  }
  // return a pair of status and variant
  return {
    status: status.toUpperCase(),
    variant: variant as
      | 'neutralOutlined'
      | 'success'
      | 'warn'
      | 'error'
      | 'brandedLight',
  };
};

export const ApiKeyTable = ({
  rows,
  currentPage,
  pageSize,
  totalCount,
  onPageChange,
  onSort,
  onRename,
  onRevoke,
}: ApiKeyTableProps) => {
  return (
    <>
      <TableContainer>
        <Table<
          | 'name'
          | 'type'
          | 'role'
          | 'entity'
          | 'createdBy'
          | 'createdAt'
          | 'lastUsed'
          | 'status'
        >
          variant="plain"
          size="condensed"
          defaultSortBy="name"
          onRequestSort={({ sortBy, sortOrder }) => {
            onSort(sortBy, sortOrder);
          }}
        >
          <Thead>
            <Tr>
              <Th>
                <Group>
                  Key Name <TableSortButton attribute="name" />
                </Group>
              </Th>
              <Th>
                <Group>
                  Entity <TableSortButton attribute="entity" />
                </Group>
              </Th>
              <Th>
                <Group>
                  Type <TableSortButton attribute="type" />
                </Group>
              </Th>
              <Th>
                <Group>
                  Role <TableSortButton attribute="role" />
                </Group>
              </Th>
              <Th>
                <Group>
                  Created By <TableSortButton attribute="createdBy" />
                </Group>
              </Th>
              <Th>
                <Group>
                  Created At <TableSortButton attribute="createdAt" />
                </Group>
              </Th>
              <Th>
                <Group>
                  Last Used <TableSortButton attribute="lastUsed" />
                </Group>
              </Th>
              <Th>
                <Group>
                  Status <TableSortButton attribute="status" />
                </Group>
              </Th>
              <Th isFormElement />
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row) => (
              <Tr key={row.name}>
                <Td>{row.name}</Td>
                <Td>{row.entity}</Td>
                <BadgeCell text={row.type} variant="neutralOutlined" />
                <BadgeCell text={row.role} variant="neutralOutlined" />
                <Td>
                  <Group gap={2}>
                    <Avatar size="sm" variant="user" />
                    <span>{row.createdBy}</span>
                  </Group>
                </Td>
                <Td>
                  <div className="truncate text-base">
                    <LocalTimestamp value={row.createdAt} format="date-only" />
                  </div>
                </Td>
                <Td>
                  <div className="truncate text-base">
                    <RelativeTimestamp value={row.lastUsed} />
                  </div>
                </Td>
                <BadgeCell
                  text={getStatusVariant(row.status).status}
                  variant={getStatusVariant(row.status).variant}
                />
                <Td>
                  <Menu placement="bottom-end" isLazy autoSelect={false}>
                    <MenuButton as={IconButton} variant="secondary">
                      <IconOverflow />
                    </MenuButton>
                    <MenuContent>
                      <MenuList>
                        <MenuItem onClick={() => onRename?.(row)}>
                          Rename
                        </MenuItem>
                        <MenuItem
                          className="text-error hover:bg-error hover:text-error"
                          onClick={() => onRevoke?.(row)}
                        >
                          Revoke
                        </MenuItem>
                      </MenuList>
                    </MenuContent>
                  </Menu>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
      <Pagination
        currentPage={currentPage}
        totalCount={totalCount}
        pageSize={pageSize}
        onChangePage={onPageChange}
      />
    </>
  );
};
